import { useState, useCallback, ReactNode } from 'react';
import styled from 'styled-components';
import { useUserContext } from '../../../../../contexts/UserContext';

import type {
  ServicePartner,
  SectionView,
} from '../../../../../components/PageWrappers/AdDetails/types';

import { WhitePanel } from '../../../../../components/Toolkit/WhitePanel/WhitePanel';
import { InfoTitle } from '../../../../../components/Toolkit/InfoTitle/InfoTitle';
import { Title } from '../../../../../components/Toolkit/InfoTitle/Title/Title';
import { KeyInfoList } from '../../../../../components/Toolkit/KeyInfoList/KeyInfoList';
import { KeyInfoCard } from '../../../../../components/Toolkit/KeyInfoCard/KeyInfoCard';
import { OptionalNextLink } from '../../../../../components/Toolkit/OptionalNextLink/OptionalNextLink';
import { SimpleShowMore } from '../../../../../components/Toolkit/SimpleShowMore/SimpleShowMore';
import { Notification } from '../../../../../components/Toolkit/Notification/Notification';
import { OverflowFade } from '../../../../../components/Toolkit/OverflowFade/OverflowFade';
import { media } from '../../../../../helpers/breakpoints';
import { pushToDataLayer } from '../../../../../services/gtmService';

import type { IAdAttributeView } from '../../../../../types';
import type { MappedVehicleFeatures } from '../../../../../components/PageWrappers/AdDetails/components/VehicleFeatures/VehicleFeatures.typed';
import type { StructuredAttributes } from '../../../../../components/PageWrappers/AdDetails/DetailedVehicleInfo/DetailedVehicleInfo.typed';
import { DetailedVehicleInfo } from '../../../../../components/PageWrappers/AdDetails/DetailedVehicleInfo/DetailedVehicleInfo';

import { CarValuationToolProvider } from '../CarValuationTool/CarValuationTool.context';
import { CarValuationTool } from '../CarValuationTool/CarValuationTool';

export interface InfoPanelProps {
  title: string;
  information: string;
  renderAction?: ReactNode;
  displayAttributes?: IAdAttributeView[];
  cdnUrl?: string;
  servicePartners?: ServicePartner[];
  section?: SectionView;
  renderDfpUnit?: ReactNode;
  isPreview?: boolean;
  className?: string;
  vehicleFeatures?: MappedVehicleFeatures[];
  structuredAttributes?: StructuredAttributes;
  isDealer: boolean;
  emailResponse: boolean;
}

const DISCLAIMER_PRIMARY =
  'All information is provided by third parties and DoneDeal are not in a position to offer any warranty or guarantee in relation to the content.';

const DISCLAIMER_SECONDARY =
  'Both buyer and seller should confirm all information in relation to the item before committing to the sale. We disclaim all liability arising out of or in connection with any reliance placed on the above content. We accept no responsibility for keeping the content and information accurate, up to date or complete.';

const SInfoTitle = styled(InfoTitle)`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
  @media (min-width: 840px) {
    margin-bottom: ${({ theme }) => theme.spacing.M24};
  }
`;

const STitle = styled(Title)`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

const StyledText = styled.p`
  font-family: inherit;
  white-space: pre-line;
`;

const SSimpleShowMore = styled(SimpleShowMore)`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
  padding: ${({ theme }) => theme.spacing.S8} 0;
  border-top: ${({ theme }) => `1px solid ${theme.colors.GREY_LIGHT}`};
  border-bottom: ${({ theme }) => `1px solid ${theme.colors.GREY_LIGHT}`};

  p {
    ${({ theme }) => theme.fontSize.M14};
  }

  ${media.large} {
    padding: ${({ theme }) => theme.spacing.M16} 0;
  }
`;

const Description = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

const ReportAd = styled.div`
  margin-top: ${({ theme }) => theme.spacing.M16};
`;

const DFP = styled.div`
  padding: ${({ theme }) => `${theme.spacing.M16} 0`};
  border-top: ${({ theme }) => `1px solid ${theme.colors.GREY_LIGHT}`};
  border-bottom: ${({ theme }) => `1px solid ${theme.colors.GREY_LIGHT}`};
  overflow: hidden;

  /* Adjust the height of the placement to avoid CLS issues */
  height: 138px;
  @media screen and (min-width: 704px) {
    height: 82px;
  }

  @media screen and (min-width: 1024px) {
    height: 138px;
  }

  @media screen and (min-width: 1080px) {
    height: 82px;
  }
`;

const KeyInfoListContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.M24};
`;

const VehicleDetailsNotification = styled(Notification)`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
  ${media.large} {
    margin-bottom: ${({ theme }) => theme.spacing.M24};
  }
`;

const ServicePartnerWrapper = styled.div`
  width: ${({ theme }) => `calc(100% + ${theme.spacing.M32})`};
  padding: ${({ theme }) =>
    `0 ${theme.spacing.M16} ${theme.spacing.M16} ${theme.spacing.M16}`};
  margin-left: -${({ theme }) => theme.spacing.M16};

  ${media.large} {
    width: ${({ theme }) => `calc(100% + ${theme.spacing.L48})`};
    padding: ${({ theme }) =>
      `0 ${theme.spacing.M24} ${theme.spacing.M24} ${theme.spacing.M24}`};
    margin-left: -${({ theme }) => theme.spacing.M24};
  }
`;

const ServicePartnerTitle = styled.p`
  ${({ theme }) => theme.fontSize.L18};
  color: ${({ theme }) => theme.colors.GREY_DARKER};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

const ServicePartnerContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  row-gap: ${({ theme }) => theme.spacing.S8};

  ${media.medium} {
    grid-template-columns: repeat(2, 1fr);
    row-gap: ${({ theme }) => theme.spacing.M16};
    column-gap: ${({ theme }) => theme.spacing.M16};
  }
`;

const InfoPanel = (props: InfoPanelProps) => {
  const {
    title,
    information,
    renderAction,
    cdnUrl,
    section,
    displayAttributes,
    servicePartners,
    renderDfpUnit,
    isPreview = false,
    className,
    vehicleFeatures,
    structuredAttributes,
    isDealer,
    emailResponse,
  } = props;

  const [isContentOverflowing, setIsContentOverflowing] = useState(false);

  const RENDER_AD_UNIT_IN_DESCRIPTION = Boolean(!servicePartners);
  const showCarValuationTool =
    !isPreview && isDealer && section?.name === 'cars';

  const isCarsOrCommercials =
    section?.name === 'cars' || section?.name === 'commercials';
  const shouldShowAIDisclaimer = isDealer && isCarsOrCommercials;

  const { setIsHistoryCheckModalOpen } = useUserContext();

  const measuredRef = useCallback((element: HTMLDivElement) => {
    if (element !== null) {
      setIsContentOverflowing(element.offsetHeight > 166);
    }
  }, []);

  function handleKeyInfoCardClick(title: string) {
    if (title === 'historyCheck') {
      setIsHistoryCheckModalOpen(true);
    }

    if (servicePartners) {
      const partner = servicePartners.filter(
        (partner) => partner.name === title,
      )[0];

      pushToDataLayer({
        event: 'partner_clicks',
        servicePartnerType: partner.link?.partnerType,
        servicePartnerName: partner.link?.partnerName,
      });
    }
  }

  return (
    <div className={className}>
      {isPreview && displayAttributes && displayAttributes?.length > 0 && (
        <WhitePanel className="common-margin-bottom">
          <KeyInfoListContainer>
            <SInfoTitle title="Key Info" listItems={['Overview']} />
            <KeyInfoList infoList={displayAttributes} cdnUrl={cdnUrl} />
          </KeyInfoListContainer>
          {isPreview && section?.name !== 'beefcattle' && (
            <VehicleDetailsNotification intent="PLAIN">
              More details about your vehicle may be automatically displayed in
              your ad
            </VehicleDetailsNotification>
          )}
        </WhitePanel>
      )}

      {structuredAttributes && structuredAttributes?.length > 0 && (
        <WhitePanel className={className}>
          <DetailedVehicleInfo
            structuredAttributes={structuredAttributes}
            vehicleFeatures={vehicleFeatures}
          />
        </WhitePanel>
      )}

      {showCarValuationTool && (
        <CarValuationToolProvider>
          <CarValuationTool emailResponse={emailResponse} />
        </CarValuationToolProvider>
      )}

      {servicePartners && (
        <WhitePanel className="common-margin-bottom">
          <ServicePartnerWrapper>
            <ServicePartnerTitle>Before you buy</ServicePartnerTitle>
            <ServicePartnerContainer>
              {servicePartners.map((servicePartner) => {
                return (
                  <OptionalNextLink
                    key={servicePartner.title}
                    href={
                      servicePartner.name !== 'historyCheck'
                        ? servicePartner?.link?.action ??
                          servicePartner?.link?.url
                        : undefined
                    }
                    isExternal={servicePartner.link?.external}
                  >
                    <KeyInfoCard
                      title={servicePartner.title}
                      linkText={servicePartner.link?.title}
                      isClickable={true}
                      onCardClick={handleKeyInfoCardClick}
                      sponsoredIllustration={servicePartner.iconPoweredBy}
                      sponsoredIllustrationText="Powered by"
                      sponsoredIllustrationAltText={`Powered by ${servicePartner.link?.partnerName}`}
                      identifier={servicePartner.name}
                      className={`${servicePartner.link?.partnerName}-${servicePartner.link?.partnerType}-imp`}
                    />
                  </OptionalNextLink>
                );
              })}
            </ServicePartnerContainer>
          </ServicePartnerWrapper>

          {renderDfpUnit && <DFP>{renderDfpUnit}</DFP>}
        </WhitePanel>
      )}

      <WhitePanel className="common-margin-bottom">
        <STitle flip={false} variant="DEFAULT" shouldTruncate={false}>
          {title}
        </STitle>

        <Description>
          <OverflowFade
            mobileContainerMaxHeight={166}
            isContentOverflowing={isContentOverflowing}
          >
            <StyledText ref={measuredRef}>{information}</StyledText>
          </OverflowFade>
        </Description>

        {shouldShowAIDisclaimer && (
          <SSimpleShowMore
            visibleText={
              "*Warranty details shown were identified using AI to directly process information from the ad's description. DoneDeal recommends checking the description and confirming with the dealership to be sure of the warranty provided in these instances."
            }
            hiddenText={`${DISCLAIMER_PRIMARY} ${DISCLAIMER_SECONDARY}`}
            showMoreText="See More"
          />
        )}

        {!shouldShowAIDisclaimer && (
          <SSimpleShowMore
            visibleText={DISCLAIMER_PRIMARY}
            hiddenText={DISCLAIMER_SECONDARY}
            showMoreText="See More"
          />
        )}

        {RENDER_AD_UNIT_IN_DESCRIPTION && renderDfpUnit && (
          <DFP>{renderDfpUnit}</DFP>
        )}

        {renderAction && <ReportAd>{renderAction}</ReportAd>}
      </WhitePanel>
    </div>
  );
};

export { InfoPanel };
